import React, { useState, useEffect, useMemo } from 'react'
import { AccountRecord, useGetAccountsInTeamQuery } from '../../api/client'
import AccountRow from '../AccountRow'
import SearchInput from '../SearchInput'
import Spinner from '../Spinner'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

interface TeamAccountsTableProps {
    teamId: number
    isAdmin: boolean
}

const ITEMS_PER_PAGE = 10

function TeamAccountsTable({ teamId, isAdmin }: TeamAccountsTableProps) {
    const [keyword, setKeyword] = useState('')
    const [currentPage, setCurrentPage] = useState(0)
    const [showAdminFeatures, setShowAdminFeatures] = useState(false)
    const [filteredAccounts, setFilteredAccounts] = useState<AccountRecord[]>([])

    // Fetch accounts for this team
    const {
        data: accountsData,
        loading: accountsLoading,
        refetch: refetchAccounts,
        error: accountsError
    } = useGetAccountsInTeamQuery({
        variables: {
            params: { teamId }
        },
        fetchPolicy: 'cache-and-network'
    })

    const accounts = useMemo(() => accountsData?.getAccountsInTeam || [], [accountsData])

    // Filter accounts based on search
    useEffect(() => {
        let filtered = accounts

        // Apply keyword filter
        if (keyword.trim()) {
            filtered = filtered.filter(
                (account) =>
                    account.accountName?.toLowerCase().includes(keyword.toLowerCase()) ||
                    account.serviceAccountUsername?.toLowerCase().includes(keyword.toLowerCase())
            )
        }

        setFilteredAccounts(filtered)
        setCurrentPage(0) // Reset to first page when filtering
    }, [accounts, keyword])

    // Paginate filtered results
    const startIndex = currentPage * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex)
    const totalPages = Math.ceil(filteredAccounts.length / ITEMS_PER_PAGE)

    const handleSearch = (searchTerm: string) => {
        setKeyword(searchTerm)
    }

    const handleRefresh = () => {
        refetchAccounts()
    }

    if (accountsError) {
        return (
            <div className="rounded-md bg-arwen-50 p-4">
                <div className="text-sm text-arwen-800">
                    Error loading profiles: {accountsError.message}
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        {/* Jost 500 override of the global Merriweather h1-h4 rule in index.css */}
                        <h3 className="font-sans !font-medium text-lg text-gray-900">
                            Team profiles
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {filteredAccounts.length} account
                            {filteredAccounts.length !== 1 ? 's' : ''}
                            {filteredAccounts.length !== accounts.length &&
                                ` of ${accounts.length} total`}
                        </p>
                    </div>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <SearchInput
                            keyword={keyword}
                            onKeywordChange={setKeyword}
                            onSearch={handleSearch}
                        />
                    </div>

                    <button
                        onClick={handleRefresh}
                        disabled={accountsLoading}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-arwen-500 disabled:opacity-50"
                    >
                        <ArrowPathIcon
                            className={clsx('h-4 w-4', accountsLoading && 'animate-spin')}
                        />
                    </button>

                    {isAdmin && (
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="admin-features"
                                checked={showAdminFeatures}
                                onChange={(e) => setShowAdminFeatures(e.target.checked)}
                                className="h-4 w-4 text-arwen-600 border-gray-300 rounded focus:ring-arwen-500"
                            />
                            <label htmlFor="admin-features" className="ml-2 text-sm text-gray-700">
                                Admin features
                            </label>
                        </div>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden">
                {accountsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Spinner size={6} />
                    </div>
                ) : paginatedAccounts.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-gray-400 text-sm">
                            {keyword
                                ? 'No accounts match your search criteria.'
                                : 'No accounts assigned to this team yet.'}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            {/* Table chrome matches the Organisations list (OrgListPage.tsx).
                                Header cells keep px-6 so they align with AccountRow's px-6 cells. */}
                            <table className="min-w-full border-collapse text-sm">
                                <thead className="bg-gray-50/60">
                                    <tr className="border-b border-gray-100">
                                        <th
                                            scope="col"
                                            className="px-6 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                        >
                                            Account
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                        >
                                            Safe
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                        >
                                            Suspect
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                        >
                                            Severe
                                        </th>
                                        {isAdmin && showAdminFeatures && (
                                            <>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                                >
                                                    Status
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                                >
                                                    Admin
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-2.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                                >
                                                    Actions
                                                </th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-50">
                                    {paginatedAccounts.map((account) => (
                                        <AccountRow
                                            key={account.id}
                                            account={account}
                                            admin={isAdmin}
                                            showAdminFeatures={showAdminFeatures}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-gray-700">
                                        Showing {startIndex + 1} to{' '}
                                        {Math.min(endIndex, filteredAccounts.length)} of{' '}
                                        {filteredAccounts.length} results
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() =>
                                                setCurrentPage(Math.max(0, currentPage - 1))
                                            }
                                            disabled={currentPage === 0}
                                            className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-sm text-gray-500">
                                            Page {currentPage + 1} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() =>
                                                setCurrentPage(
                                                    Math.min(totalPages - 1, currentPage + 1)
                                                )
                                            }
                                            disabled={currentPage >= totalPages - 1}
                                            className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default TeamAccountsTable
